import React, { useEffect, useMemo, useState } from 'react'
import { useStores, setPageTitle } from '@trivie/core'
import { Avatar, color, Header, spacing, Text } from '@trivie/ui-mobile'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import {
  ActivityIndicator,
  Image,
  SectionList,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ImageStyle,
  ViewStyle,
} from 'react-native'
import RenderHtml, { defaultSystemFonts } from 'react-native-render-html'
import { ReactComponent as NotificationsNull } from './notifications-null.svg'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { MessagesApi } from '@trivie/core/src/services/api-objects/MessagesApi'
import { useMediaQuery } from '@mui/material'
import { SubscriptionLockedScreen } from '@trivie/ui-mobile/components/subscription-locked-screen'

interface NotificationProps {
  timestamp?: any
  content?: string
  type?: any
  style?: ViewStyle
  contentOptions?: any
  category?: string
  action?: any
  text: string
  enterpriseTier?: boolean
}

const Notification: React.FunctionComponent<NotificationProps> = (props) => {
  const { timestamp, style, text } = props

  const createdAt = DateTime.fromISO(timestamp, { zone: 'utc' })
  const now = DateTime.now()
  const elapsed = now.diff(createdAt).toMillis()

  const msPerMinute = 60 * 1000
  const msPerHour = msPerMinute * 60
  const msPerDay = msPerHour * 24
  const msPerMonth = msPerDay * 30
  const msPerYear = msPerDay * 365

  let elapsedTime = ''

  if (elapsed < msPerMinute) {
    elapsedTime = 'Just Now'
  } else if (elapsed < msPerHour) {
    elapsedTime = `${Math.round(elapsed / msPerMinute)}m`
  } else if (elapsed < msPerDay) {
    elapsedTime = `${Math.round(elapsed / msPerHour)}h`
  } else if (elapsed < msPerMonth) {
    elapsedTime = `${Math.round(elapsed / msPerDay)}d`
  } else if (elapsed < msPerYear) {
    elapsedTime = `${Math.round(elapsed / msPerMonth)}M`
  } else {
    elapsedTime = `${Math.round(elapsed / msPerYear)}y`
  }

  const { width } = useWindowDimensions()
  const systemFonts = [...defaultSystemFonts, 'Inter']

  return (
    <View style={{ ...NTF_ITEM, ...style }}>
      <RenderHtml systemFonts={systemFonts} contentWidth={width} source={{ html: text }} />
      <Text style={NTF_DATE} text={elapsedTime} />
    </View>
  )
}

export const NotificationsScreen = observer(() => {
  const { subscriptionStore, userStore, surveyStore, quizStore } = useStores()
  const { adaptive, getAdaptive } = quizStore
  const { startSurvey } = surveyStore

  const [respondedToSurvey, setRespondedToSurvey] = useState(false)
  const [hasAdaptive, setHasAdaptive] = useState(adaptive?.has_quiz)
  const { user, checkNotifications, notifications } = userStore

  const isMobile = !useMediaQuery('(min-width:800px)')

  useEffect(() => {
    setPageTitle(`Notifications - ${process.env.REACT_APP_SITE_TITLE}`)
  }, [])

  const { t } = useTranslation()

  const byDate = (a: any, b: any) => {
    const x = DateTime.fromISO(a.created_at)
    const y = DateTime.fromISO(b.created_at)
    if (x > y) {
      return -1
    }
    if (x === y) {
      return 0
    }
    if (x < y) {
      return 1
    }
    return 0
  }

  const applyFilter = useMemo(
    () => (ntf) => {
      if (!user?.workspace_settings?.feed_enabled)
        return (
          ntf?.filter(
            (f) =>
              f.category.label !== 'Announcement' &&
              f.category.label !== 'Post' &&
              f.category.label !== 'Comment' &&
              f.category.label !== 'Like',
          ) || []
        )
      return ntf || []
    },
    [notifications],
  )

  const sections = [
    {
      title: t('Notifications.new'),
      visible: notifications?.new?.length > 0,
      data: applyFilter(notifications?.new?.slice()).sort(byDate),
    },
    {
      title: t('Notifications.priority'),
      visible: notifications?.priority?.length > 0,
      data: applyFilter(notifications?.priority?.slice()).sort(byDate),
    },
    {
      title: t('Challenges.header'),
      visible: notifications?.challenges?.length > 0,
      data: applyFilter(notifications?.challenges?.slice()).sort(byDate),
    },
    {
      title: t('Notifications.earlier'),
      visible: notifications?.earlier?.length > 0,
      data: applyFilter(notifications?.earlier?.slice()).sort(byDate),
    },
  ]

  useEffect(() => {
    if (user) {
      getAdaptive(user.id).then(() => {
        setHasAdaptive(adaptive?.has_quiz || false)
      })
    }
  }, [])

  const read = (notification: any) => {
    const { object_type, object_id, action } = notification
    if (object_type === 'Message') {
      MessagesApi.getMessage(object_id).then((resp) => {
        if (resp.ok) {
          setMessage(resp.data)
          setShowMessage(true)
        }
      })
    } else if (
      object_type === 'Badge' ||
      object_type === 'UserLevel' ||
      object_type === 'AssignmentUserMedal' ||
      object_type === 'Certification'
    ) {
      if (user?.id) {
        checkNotifications(user.id, notification.id, false)
      }
    } else if (notification.permalink) {
      if (action === 'reinforcement' && !hasAdaptive) {
        location.replace('/play')
      } else if (object_type === 'Survey') {
        startSurvey(object_id, user!.id).then((resp) => {
          if (resp?.responded) {
            setRespondedToSurvey(true)
          } else {
            setRespondedToSurvey(false)
            location.replace(notification.permalink)
          }
        })
      } else {
        location.replace(notification.permalink)
      }
    }
  }

  const [showMessage, setShowMessage] = useState(false)
  const [message, setMessage] = useState<any>({ subject: '', message_text: '' })

  const renderItem = (item) => {
    return (
      <TouchableOpacity
        // @ts-ignore
        dataSet={{ qat: `notification_id_${item.id}` }}
        accessibilityRole="link"
        accessibilityLabel="Go to event"
        onPress={() => read(item)}
        style={[NTF_CONTAINER, { backgroundColor: !item.viewed_at ? '#F4FBFF' : color.white }]}
      >
        {item.user_full_name ? (
          <Avatar
            style={NTF_AVATAR}
            size="small"
            source={item?.source_user_photo_url}
            name={item?.user_full_name}
          />
        ) : (
          <Avatar
            style={NTF_AVATAR}
            size="small"
            source={user?.photo_url}
            name={`${user?.first_name} ${user?.last_name}`}
          />
        )}
        <Image source={{ uri: item?.category?.full_url.replace(/[(),]/g, '') }} style={NTF_IMAGE} />
        <Notification text={item.text} timestamp={item.created_at} />
      </TouchableOpacity>
    )
  }

  const renderList = () => {
    if (isMobile) {
      return (
        <>
          {sections.map((e, i) => {
            if (e.data?.length > 0) {
              return (
                <div>
                  <p>
                    <Text variant="labelMedium" text={e.title} />
                  </p>
                  {e.data.map((item) => renderItem(item))}
                </div>
              )
            }
          })}
        </>
      )
    } else {
      return (
        <SectionList
          contentContainerStyle={{ paddingHorizontal: 16, flex: 1 }}
          showsVerticalScrollIndicator={false}
          stickySectionHeadersEnabled
          scrollEnabled={isMobile}
          sections={sections}
          keyExtractor={(item: any) => item.id.toString()}
          renderItem={({ item }) => renderItem(item)}
          renderSectionHeader={({ section: { title, visible } }) => {
            return visible ? (
              <View style={SECTION_HEADER}>
                <Text variant="labelEmphasized" text={title || ''} />
              </View>
            ) : (
              <></>
            )
          }}
        />
      )
    }
  }

  const isSubscriptionWorkspace = userStore.isSubscriptionWorkspace
  const hasActive = subscriptionStore.hasActiveSubscriptions

  if (isSubscriptionWorkspace && !hasActive) {
    return (
      <View testID="talk-screen" style={{ flex: 1, height: '100%', maxWidth: 800 }}>
        <div
          style={{
            height: '100%',
            flexDirection: 'column',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <div style={HEADER_STICKY_CONTAINER}>
            <div style={HEADER_FLEX_CONTAINER}>
              <div style={HEADER_ITEM1}>
                <Header titleStyle={HEADER} headerTx="Notifications.header" />
              </div>
            </div>
          </div>
          <SubscriptionLockedScreen
            emoji="📚"
            title="No active subscriptions"
            helperText="Purchase or renew a subscription to start learning"
            buttonText="Browse Products"
            onButtonPress={
              userStore.externalStoreUrl
                ? () => {
                    window.open(
                      // @ts-ignore
                      userStore.externalStoreUrl,
                      '_blank',
                      'noopener',
                    )
                  }
                : undefined
            }
          />
        </div>
      </View>
    )
  }

  return (
    <View testID="ActivityScreen" style={FULL}>
      <div style={CONTAINER}>
        <div style={HEADER_STICKY_CONTAINER}>
          <div style={HEADER_FLEX_CONTAINER}>
            <div style={HEADER_ITEM1}>
              <Header titleStyle={HEADER} headerTx="Notifications.header" />
            </div>
          </div>
        </div>
        {userStore.isLoading ? (
          <View style={{ flex: 1, height: '100%', top: -60, justifyContent: 'center' }}>
            <ActivityIndicator color="#999" size="small" style={{ alignSelf: 'center' }} />
          </View>
        ) : notifications?.new?.length > 0 ||
          notifications?.priority?.length > 0 ||
          notifications?.earlier?.length > 0 ||
          notifications?.challenges?.length > 0 ? (
          renderList()
        ) : (
          <View style={NULL_STATE}>
            <NotificationsNull />
            <Text
              style={{ width: 311, color: color.darkenGrey, paddingTop: 24, textAlign: 'center' }}
              variant="body1Medium"
              tx="Settings.Notifications.nullText"
            />
          </View>
        )}
      </div>
      {respondedToSurvey && (
        <Dialog
          onClose={() => setRespondedToSurvey(false)}
          title="Survey"
          dialogText="You have already responded to this survey."
          maxWidth="xs"
          onConfirm={() => setRespondedToSurvey(false)}
          aria-labelledby="confirmation-dialog-title"
          open={respondedToSurvey}
        />
      )}
      {showMessage && (
        <Dialog
          sx={{ textAlign: 'left' }}
          onClose={() => setShowMessage(false)}
          title={message.subject}
          dialogText={message.message_text}
          maxWidth="sm"
          onConfirm={() => setShowMessage(false)}
          aria-labelledby="confirmation-dialog-title"
          open={showMessage}
        />
      )}
    </View>
  )
})

/**
 * Styling
 */
const FULL: ViewStyle = {
  display: 'flex',
  height: '100%',
  flex: 1,
}
const CONTAINER: React.CSSProperties = {
  height: '100%',
  paddingBottom: 100,
  backgroundColor: color.white,
}
const NULL_STATE: ViewStyle = {
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
  height: '100%',
  top: -60,
}
const HEADER: TextStyle = { fontSize: 24, color: color.black }
const SECTION_HEADER: ViewStyle = {
  backgroundColor: color.white,
  paddingTop: spacing[4],
  paddingBottom: spacing[2],
}

const NTF_IMAGE: ImageStyle = {
  height: 32,
  width: 32,
  borderRadius: 100,
  top: 23,
  zIndex: 999,
  right: 8,
}
const NTF_ITEM: ViewStyle = {
  flex: 1,
  flexDirection: 'column',
  minHeight: 78,
  justifyContent: 'center',
  paddingLeft: 4,
}
const NTF_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  borderBottomColor: 'rgba(228, 228, 228, 0.3)',
  borderBottomWidth: 1,
  marginBottom: 10,
  borderRadius: 8,
  paddingLeft: 12,
  paddingRight: 12,
}
const NTF_AVATAR: ViewStyle = { top: spacing[4] + 3 }

const NTF_DATE: TextStyle = {
  lineHeight: 14.52,
  fontWeight: '400',
  fontSize: 12,
  top: 4,
  color: color.shade50,
}

const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}

const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER_ITEM1: React.CSSProperties = {
  width: '100%',
}
